import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getMemberFinance,
  getFlightsByPilot,
  getPilotChecks,
  getUsers,
  downloadUserDocument,
  getUserDocumentFile,
  getUserDocuments,
} from '../../api';
import { Flight, PilotCheckItem, PilotChecks, UserDocument } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { DateFormat, DateFormatStyle } from '../../utils/DateFormat';
import { formatHours } from '../../utils/flightDisplay';
import { formatMoney } from '../../utils/formatMoney';
import { getApiErrorMessage } from '../../utils/apiError';
import { isGeneralUser } from '../../utils/isGeneralUser';
import {
  blobWithMime,
  previewKindFromName,
  type PreviewKind,
} from '../../utils/filePreview';
import DocumentPreviewModal from '../../components/DocumentPreviewModal/DocumentPreviewModal';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import PilotBadge from '../../components/PilotBadge/PilotBadge';
import ProfileSettingsModal from '../../components/ProfileSettingsModal/ProfileSettingsModal';
import { parseProfileUserId, profileBasePath } from './profilePaths';
import './ProfilePage.css';

type SettingsTab = 'general' | 'notifications' | 'password';
type HubDocType = 'ppl' | 'vlek' | 'skills' | 'other';

const parseSettingsTab = (value: string | null): SettingsTab | null => {
  if (value === 'general' || value === 'notifications' || value === 'password') {
    return value;
  }
  return null;
};

const emptyChecks = (): PilotChecks => ({
  birth_date: null,
  ppl: { last_date: null, next_date: null, overdue: false, days_left: null },
  vlek: { last_date: null, next_date: null, overdue: false, days_left: null },
  annual: { last_date: null, next_date: null, overdue: false, days_left: null },
  skills: { last_date: null, next_date: null, overdue: false, days_left: null },
});

const checkLine = (
  item: PilotCheckItem,
  dateFormat: DateFormatStyle,
  locked?: boolean
) => {
  if (locked) return 'Нужна ДР';
  if (item.needs_birth_date) return 'Нужна ДР';
  if (item.overdue) {
    return item.next_date
      ? `Просрочено с ${DateFormat(item.next_date, dateFormat)}`
      : 'Просрочено';
  }
  if (!item.last_date) return 'Не пройдено';
  if (item.next_date) {
    if (item.days_left != null && item.days_left <= 30) {
      return `до ${DateFormat(item.next_date, dateFormat)} (${item.days_left} дн.)`;
    }
    return `до ${DateFormat(item.next_date, dateFormat)}`;
  }
  return `пройдено ${DateFormat(item.last_date, dateFormat)}`;
};

const filesCountLabel = (count: number) => {
  const n10 = count % 10;
  const n100 = count % 100;
  if (n10 === 1 && n100 !== 11) return `${count} файл`;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return `${count} файла`;
  return `${count} файлов`;
};

type HubCardProps = {
  to?: string;
  label: string;
  value: string;
  unit?: string;
  footer: string;
};

const ProfileHubCard = ({ to, label, value, unit, footer }: HubCardProps) => {
  const inner = (
    <>
      <span className="profile-hub-label">{label}</span>
      <span className="profile-hub-value">
        <span className="profile-hub-number">
          {value}
          {value !== '—' && unit ? (
            <span className="profile-hub-unit">{unit}</span>
          ) : null}
        </span>
      </span>
      <span className="profile-hub-footer">{footer}</span>
    </>
  );

  if (!to) {
    return <div className="profile-hub-card is-static">{inner}</div>;
  }

  return (
    <Link to={to} className="profile-hub-card">
      {inner}
    </Link>
  );
};

const ProfilePage = () => {
  const { userId: userIdParam } = useParams();
  const {
    currentUserId,
    currentUserName,
    user,
    clubs,
    isAdmin,
    isTechnician,
    patchUser,
    switchClub,
  } = useAuth();
  const { aircraftId, dataRevision } = useAircraft();
  const { showError, feedbackNode } = useFeedback();
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsFromQuery = parseSettingsTab(searchParams.get('settings'));
  const [clubSwitching, setClubSwitching] = useState(false);

  const paramId = parseProfileUserId(userIdParam);
  const targetUserId = paramId ?? currentUserId;
  const isOwn = targetUserId === currentUserId;
  const canEditDocuments = isOwn || isAdmin;
  const basePath = profileBasePath(targetUserId, currentUserId);
  const activeClubId =
    user?.club_id ?? clubs.find((c) => c.active)?.club_id ?? 0;

  const [checks, setChecks] = useState<PilotChecks>(emptyChecks());
  const [personalHours, setPersonalHours] = useState(0);
  const [hourCost, setHourCost] = useState<number | null>(null);
  const [subjectName, setSubjectName] = useState(currentUserName || '—');
  const [color, setColor] = useState<string | null>(null);
  const [hideColors, setHideColors] = useState(Boolean(user?.hide_colors));
  const [dateFormat, setDateFormat] = useState<DateFormatStyle>(
    user?.date_format === 'long' ? 'long' : 'dot'
  );
  const [settingsOpen, setSettingsOpen] = useState(
    Boolean(settingsFromQuery) && isOwn
  );
  const [settingsTab, setSettingsTab] = useState<SettingsTab | undefined>(
    settingsFromQuery ?? undefined
  );
  const [forbidden, setForbidden] = useState(false);
  const loadReqId = useRef(0);
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [previewFiles, setPreviewFiles] = useState<UserDocument[]>([]);
  const [previewDoc, setPreviewDoc] = useState<UserDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewKind>('other');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequest = useRef(0);

  useEffect(() => {
    if (!isOwn) return;
    const tab = parseSettingsTab(searchParams.get('settings'));
    if (!tab) return;
    setSettingsTab(tab);
    setSettingsOpen(true);
  }, [searchParams, isOwn]);

  useEffect(() => {
    if (userIdParam && paramId == null) {
      setForbidden(true);
      return;
    }
    if (!isOwn && !isAdmin) {
      setForbidden(true);
      return;
    }
    setForbidden(false);
  }, [userIdParam, paramId, isOwn, isAdmin]);

  useEffect(() => {
    const reqId = ++loadReqId.current;
    if (isTechnician || forbidden) {
      return () => {
        loadReqId.current += 1;
      };
    }
    const load = async () => {
      try {
        const [checksRes, flightsRes, usersRes, financeRes, docsRes] = await Promise.all([
          getPilotChecks(targetUserId),
          aircraftId > 0
            ? getFlightsByPilot(targetUserId)
            : Promise.resolve({ data: [] as Flight[] }),
          getUsers(true),
          aircraftId > 0
            ? getMemberFinance(targetUserId)
            : Promise.resolve({
                data: {
                  hour_cost: null as number | null,
                  hour_cost_all: null as number | null,
                },
              }),
          getUserDocuments(targetUserId).catch(() => ({
            data: [] as UserDocument[],
          })),
        ]);
        if (reqId !== loadReqId.current) return;
        const subject = usersRes.data.find((u) => u.id === targetUserId);
        if (!subject || isGeneralUser(subject)) {
          setForbidden(true);
          return;
        }
        if (
          !isOwn &&
          user?.club_role !== 'owner' &&
          subject.role !== 'pilot'
        ) {
          setForbidden(true);
          return;
        }

        setChecks(checksRes.data);
        setDocuments(docsRes.data || []);
        const flights = flightsRes.data as Flight[];
        const hours =
          Math.round(
            flights.reduce((sum, f) => sum + (f.hours || 0), 0) * 100
          ) / 100;
        setPersonalHours(hours);
        setHourCost(
          financeRes.data.hour_cost_all ?? financeRes.data.hour_cost ?? null
        );
        setSubjectName(subject.name || '—');
        setColor(subject.color ?? null);

        if (isOwn) {
          setHideColors(Boolean(subject.hide_colors));
          patchUser({
            color: subject.color ?? null,
            hide_colors: Boolean(subject.hide_colors),
            date_format:
              subject.date_format === 'long' || subject.date_format === 'dot'
                ? subject.date_format
                : undefined,
          });
          if (
            subject.date_format === 'long' ||
            subject.date_format === 'dot'
          ) {
            setDateFormat(subject.date_format);
          }
        } else if (
          subject.date_format === 'long' ||
          subject.date_format === 'dot'
        ) {
          setDateFormat(subject.date_format);
        }
      } catch (error) {
        if (reqId !== loadReqId.current) return;
        showError(getApiErrorMessage(error, 'Ошибка загрузки личного кабинета'));
      }
    };
    void load();
    return () => {
      loadReqId.current += 1;
    };
  }, [
    targetUserId,
    isOwn,
    patchUser,
    aircraftId,
    dataRevision,
    showError,
    isTechnician,
    forbidden,
    user?.club_id,
    user?.club_role,
  ]);

  const handleClubChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    if (!Number.isFinite(id) || id === activeClubId) return;
    setClubSwitching(true);
    try {
      await switchClub(id);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось сменить клуб'));
    }
    setClubSwitching(false);
  };

  const closePreview = () => {
    previewRequest.current += 1;
    setPreviewDoc(null);
    setPreviewFiles([]);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewKind('other');
    setPreviewUrl(null);
  };

  const handlePreview = async (doc: UserDocument) => {
    const requestId = previewRequest.current + 1;
    previewRequest.current = requestId;
    const name = doc.original_name || doc.filename;
    const kind = previewKindFromName(name);
    setPreviewUrl(null);
    setPreviewDoc(doc);
    setPreviewKind(kind);
    setPreviewError(null);
    if (kind === 'other') {
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await getUserDocumentFile(targetUserId, doc.id);
      if (previewRequest.current !== requestId) return;
      const blob = blobWithMime(res.data, name);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      if (previewRequest.current !== requestId) return;
      setPreviewError(
        getApiErrorMessage(error, 'Не удалось открыть документ')
      );
    }
    if (previewRequest.current === requestId) {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (doc: UserDocument) => {
    try {
      await downloadUserDocument(
        targetUserId,
        doc.id,
        doc.original_name || doc.filename
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось скачать документ'));
    }
  };

  const handleOpenDoc = (type: HubDocType) => {
    const files = documents.filter((item) => item.doc_type === type);
    if (files.length === 0) return;
    setPreviewFiles(files);
    void handlePreview(files[0]);
  };

  const previewIndex = previewDoc
    ? previewFiles.findIndex((item) => item.id === previewDoc.id)
    : -1;

  const goPreview = (index: number) => {
    const doc = previewFiles[index];
    if (!doc) return;
    void handlePreview(doc);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (forbidden) {
    return <Navigate to="/profile" replace />;
  }

  const hasBirth = Boolean(checks.birth_date);
  const hourCostValue =
    hourCost == null || !Number.isFinite(hourCost)
      ? '—'
      : formatMoney(hourCost);
  const otherDocsCount = documents.filter(
    (item) => item.doc_type === 'other'
  ).length;

  const renderDocRow = (
    type: HubDocType,
    label: string,
    value: string,
    warn?: boolean
  ) => {
    const hasFile = documents.some((item) => item.doc_type === type);
    if (!hasFile) {
      return (
        <div className="profile-data-row">
          <dt>{label}</dt>
          <dd className={warn ? 'is-warn' : undefined}>{value}</dd>
        </div>
      );
    }
    return (
      <div className="profile-data-row is-openable has-file">
        <button
          type="button"
          className="profile-data-row-open"
          onClick={() => handleOpenDoc(type)}
          aria-label={`Открыть ${label}`}
        >
          <span className="profile-data-dt">{label}</span>
          <span className={`profile-data-dd${warn ? ' is-warn' : ''}`}>
            {value}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className="profile-page">
      {feedbackNode}
      <div className="profile-header">
        <div className="profile-header-text">
          {!isOwn ? (
            <Link to="/club" className="profile-back-link">
              ← Клуб
            </Link>
          ) : (
            <PageBackLink to="/" />
          )}
          <h2>Личный кабинет пилота</h2>
          {isOwn ? (
            <div className="profile-club-switch">
              {clubs.length > 1 ? (
                <select
                  className="profile-club-select"
                  value={activeClubId || ''}
                  disabled={clubSwitching}
                  onChange={(e) => void handleClubChange(e)}
                  aria-label="Выбор клуба для личного кабинета"
                >
                  {clubs.map((c) => (
                    <option key={c.club_id} value={c.club_id}>
                      {c.club_name}
                    </option>
                  ))}
                </select>
              ) : user?.club_name ? (
                <p className="profile-club-name">{user.club_name}</p>
              ) : null}
            </div>
          ) : null}
        </div>
        {isOwn && !isTechnician ? (
          <button
            type="button"
            className="btn-profile-settings"
            onClick={() => {
              setSettingsTab(undefined);
              setSettingsOpen(true);
            }}
            aria-label="Настройки"
            title="Настройки"
          >
            <img src="/settings.png" alt="" className="settings-icon" />
          </button>
        ) : null}
      </div>

      {isTechnician && isOwn ? (
        <p className="profile-role-hint">
          Полный личный кабинет недоступен для роли «техник». Можно сменить клуб
          или выйти.
        </p>
      ) : (
        <>
          <section className="profile-data" aria-labelledby="profile-data-title">
            <div className="profile-data-head">
              <h3 id="profile-data-title">
                {isOwn ? 'Мои данные' : 'Данные пилота'}
              </h3>
              <Link
                to={`${basePath}/documents`}
                className="profile-data-edit"
              >
                {canEditDocuments ? 'Изменить' : 'Открыть'}
              </Link>
            </div>
            <dl className="profile-data-list">
              <div className="profile-data-row">
                <dt>Имя</dt>
                <dd>
                  <PilotBadge name={subjectName} color={color} />
                </dd>
              </div>
              {isOwn && user?.login ? (
                <div className="profile-data-row">
                  <dt>Логин</dt>
                  <dd>{user.login}</dd>
                </div>
              ) : null}
              <div className="profile-data-row">
                <dt>ДР</dt>
                <dd>
                  {checks.birth_date
                    ? DateFormat(checks.birth_date, dateFormat)
                    : 'Не указана'}
                </dd>
              </div>
              {renderDocRow(
                'ppl',
                'Свидетельство',
                checks.ppl?.last_date
                  ? DateFormat(checks.ppl.last_date, dateFormat)
                  : 'Не указано'
              )}
              {renderDocRow(
                'vlek',
                'ВЛЭК',
                checkLine(checks.vlek, dateFormat, !hasBirth),
                checks.vlek.overdue || (!hasBirth && !checks.vlek.last_date)
              )}
              {renderDocRow(
                'skills',
                'Проверка навыков',
                checkLine(checks.skills, dateFormat),
                checks.skills.overdue
              )}
              {renderDocRow(
                'other',
                'Иные',
                otherDocsCount ? filesCountLabel(otherDocsCount) : 'Нет'
              )}
              <div className="profile-data-row">
                <dt>Осмотр</dt>
                <dd className={checks.annual.overdue ? 'is-warn' : undefined}>
                  {checkLine(checks.annual, dateFormat)}
                </dd>
              </div>
            </dl>
          </section>

          <div className="profile-hub-grid">
            <ProfileHubCard
              to={`${basePath}/flights`}
              label={isOwn ? 'Мои полёты' : 'Полёты'}
              value={formatHours(personalHours)}
              unit="м/ч"
              footer={isOwn ? 'Личный налёт' : 'Налёт пилота'}
            />
            <ProfileHubCard
              to={`${basePath}/finance`}
              label={isOwn ? 'Мои финансы' : 'Финансы'}
              value={hourCostValue}
              unit="₽"
              footer={
                isOwn
                  ? 'Моя стоимость летного часа'
                  : 'Стоимость летного часа'
              }
            />
          </div>
        </>
      )}

      {isOwn && !isTechnician && settingsOpen && (
        <ProfileSettingsModal
          userId={currentUserId}
          isAdmin={isAdmin}
          initialName={user?.name || subjectName}
          initialLogin={user?.login || ''}
          initialColor={color}
          initialHideColors={hideColors}
          initialDateFormat={dateFormat}
          initialTab={settingsTab}
          onClose={() => {
            setSettingsOpen(false);
            setSettingsTab(undefined);
            if (searchParams.has('settings')) {
              const next = new URLSearchParams(searchParams);
              next.delete('settings');
              setSearchParams(next, { replace: true });
            }
          }}
          onColorChange={setColor}
          onHideColorsChange={setHideColors}
          onDateFormatChange={setDateFormat}
          onProfileChange={({ name }) => setSubjectName(name)}
        />
      )}

      {previewDoc ? (
        <DocumentPreviewModal
          title={previewDoc.original_name || previewDoc.filename}
          objectUrl={previewUrl}
          kind={previewKind}
          loading={previewLoading}
          error={previewError}
          fileIndex={Math.max(0, previewIndex)}
          fileCount={previewFiles.length}
          onPrev={() => goPreview(previewIndex - 1)}
          onNext={() => goPreview(previewIndex + 1)}
          onDownload={() => void handleDownload(previewDoc)}
          onClose={closePreview}
        />
      ) : null}
    </div>
  );
};

export default ProfilePage;
